if (!customElements.get('variant-metafield')) {
  class VariantMetafield extends HTMLElement {
    constructor() {
      super();
      this.currentVariantId = null;
      this.metafieldKey = null;
      this.variantMetafields = null;
      this.productCard = null;
      this.productForm = null;
      this.variantSelector = null;
      this.variantObserver = null;
      this.boundHandleVariantChange = null;
      this.boundHandleVariantSelectorChange = null;
    }

    connectedCallback() {
      this.initialize();
      this.setupEventListeners();
      this.updateContent();
    }

    disconnectedCallback() {
      this.cleanup();
    }

    initialize() {
      // Get metafield key from data attribute
      const metafieldAttr = this.getAttribute('data-metafield');
      if (!metafieldAttr) {
        console.warn('VariantMetafield: No data-metafield attribute found');
        return;
      }

      this.metafieldKey = metafieldAttr;

      // Get variant metafields data from data attribute
      const variantMetafieldsData = this.getAttribute('data-variant-metafields');
      if (variantMetafieldsData) {
        try {
          this.variantMetafields = JSON.parse(variantMetafieldsData);
        } catch (e) {
          console.error('VariantMetafield: Failed to parse variant metafields data', e);
          console.error('VariantMetafield: Raw data:', variantMetafieldsData);
          console.error('VariantMetafield: Data length:', variantMetafieldsData.length);
          this.variantMetafields = null;
        }
      }

      // Get product handle for matching with form
      const productHandle = this.getAttribute('data-product-handle');

      // First, try to find closest product-card element
      this.productCard = this.closest('product-card');
      
      if (!this.productCard) {
        // If no product-card, look for main-product-form with matching product handle
        if (productHandle) {
          const form = document.getElementById('main-product-form');
          if (form) {
            const formProductHandle = form.getAttribute('data-product-handle');
            if (formProductHandle === productHandle) {
              this.productForm = form;
              // Find the variant selector within the form
              this.variantSelector = form.querySelector('select[name="id"]');
              if (!this.variantSelector) {
                console.warn('VariantMetafield: Found main-product-form but no variant selector found');
                return;
              }
            } else {
              console.warn(`VariantMetafield: main-product-form found but product handle mismatch. Expected: ${productHandle}, Found: ${formProductHandle}`);
              return;
            }
          } else {
            console.warn('VariantMetafield: No parent product-card element found and no main-product-form found');
            return;
          }
        } else {
          console.warn('VariantMetafield: No parent product-card element found and no data-product-handle attribute');
          return;
        }
      }

      // Get initial variant ID
      this.updateCurrentVariantId();
    }

    updateCurrentVariantId() {
      if (this.productCard) {
        // Get variant ID from product-card
        const variantIdAttr = this.productCard.getAttribute('data-variant-id');
        if (variantIdAttr) {
          this.currentVariantId = parseInt(variantIdAttr);
        }
      } else if (this.variantSelector) {
        // Get variant ID from variant selector
        const variantId = this.variantSelector.value;
        if (variantId) {
          this.currentVariantId = parseInt(variantId);
        }
      }
    }

    setupEventListeners() {
      if (this.productCard) {
        // Setup listeners for product-card context
        this.boundHandleVariantChange = this.handleVariantChange.bind(this);
        
        // Watch for changes to data-variant-id on the product-card
        this.variantObserver = new MutationObserver(() => {
          const oldVariantId = this.currentVariantId;
          this.updateCurrentVariantId();
          if (oldVariantId !== this.currentVariantId) {
            this.updateContent();
          }
        });

        // Observe the product-card for data-variant-id changes
        this.variantObserver.observe(this.productCard, {
          attributes: true,
          attributeFilter: ['data-variant-id']
        });

        // Also listen for custom variant change events
        document.addEventListener('product-options:changed', this.boundHandleVariantChange);
      } else if (this.variantSelector) {
        // Setup listeners for product form context
        this.boundHandleVariantSelectorChange = this.handleVariantSelectorChange.bind(this);
        this.boundHandleVariantChange = this.handleVariantChange.bind(this);
        
        // Listen for changes to the variant selector
        this.variantSelector.addEventListener('change', this.boundHandleVariantSelectorChange);
        
        // Also listen for custom variant change events
        document.addEventListener('product-options:changed', this.boundHandleVariantChange);
      }
    }

    handleVariantSelectorChange(event) {
      // Update variant ID from variant selector
      const variantId = parseInt(event.target.value);
      if (!variantId) return;
      
      const oldVariantId = this.currentVariantId;
      if (variantId !== oldVariantId) {
        this.currentVariantId = variantId;
        this.updateContent();
      }
    }

    handleVariantChange(event) {
      // Update variant ID from product-card or variant selector in case it changed
      const oldVariantId = this.currentVariantId;
      this.updateCurrentVariantId();
      if (oldVariantId !== this.currentVariantId) {
        this.updateContent();
      }
    }

    getMetafieldValue(variantId) {
      if (!this.variantMetafields || !variantId) {
        return null;
      }

      // Normalize variant ID to number for comparison
      const normalizedVariantId = typeof variantId === 'string' ? parseInt(variantId, 10) : variantId;

      // Find metafield for this variant - compare both as numbers to handle type mismatches
      const variantData = this.variantMetafields.find(v => {
        const vId = typeof v.id === 'string' ? parseInt(v.id, 10) : v.id;
        return vId === normalizedVariantId;
      });
      if (!variantData) {
        return null;
      }

      // Get metafield value using the metafield key
      // Metafield key format: "namespace.key" (e.g., "hp_discount.discount_json")
      const metafieldParts = this.metafieldKey.split('.');
      if (metafieldParts.length !== 2) {
        console.warn('VariantMetafield: Invalid metafield key format. Expected "namespace.key"');
        return null;
      }

      const namespace = metafieldParts[0];
      const key = metafieldParts[1];

      // Check if variantData has the metafield
      if (variantData.metafields && variantData.metafields[namespace] && variantData.metafields[namespace][key]) {
        const metafieldData = variantData.metafields[namespace][key];
        
        // The metafield data is now stored directly as the value
        // For JSON types, it's already parsed JSON
        // For non-JSON types, it's the raw value (string, number, etc.)
        return metafieldData;
      }

      return null;
    }

    updateContent() {
      if (!this.currentVariantId) {
        this.textContent = '';
        return;
      }

      const metafieldValue = this.getMetafieldValue(this.currentVariantId);
      
      if (metafieldValue !== null && metafieldValue !== undefined && metafieldValue !== '') {
        // Handle different metafield types
        if (typeof metafieldValue === 'string') {
          // If it's a JSON string, try to parse it
          try {
            const parsed = JSON.parse(metafieldValue);
            // If parsed successfully and it's an object/array, display as formatted JSON
            if (typeof parsed === 'object') {
              this.textContent = JSON.stringify(parsed, null, 2);
            } else {
              this.textContent = metafieldValue;
            }
          } catch (e) {
            // Not JSON, just display as string
            this.textContent = metafieldValue;
          }
        } else if (typeof metafieldValue === 'object') {
          // Already an object, stringify it
          this.textContent = JSON.stringify(metafieldValue, null, 2);
        } else {
          // Number, boolean, etc.
          this.textContent = String(metafieldValue);
        }
      } else {
        // No metafield value for this variant
        this.textContent = '';
      }
    }

    cleanup() {
      if (this.variantObserver) {
        this.variantObserver.disconnect();
        this.variantObserver = null;
      }
      
      if (this.boundHandleVariantChange) {
        document.removeEventListener('product-options:changed', this.boundHandleVariantChange);
        this.boundHandleVariantChange = null;
      }
      
      if (this.variantSelector && this.boundHandleVariantSelectorChange) {
        this.variantSelector.removeEventListener('change', this.boundHandleVariantSelectorChange);
        this.boundHandleVariantSelectorChange = null;
      }
    }
  }

  customElements.define('variant-metafield', VariantMetafield);
}

